import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Select, Store } from '@ngxs/store';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { AcceptOrStartCallAction, DisconnectLineA, StartNewCallAction, ToggleHoldLineA } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { TransferState } from 'src/app/store/samwin/transfer/transfer.state';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';

import { faHistory } from '@fortawesome/pro-duotone-svg-icons';
import { faPause } from '@fortawesome/pro-regular-svg-icons';
import { map, takeUntil, tap } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { isAnonymous } from 'src/app/helper/peerHelper';

import { ThemeType } from 'src/app/samwebDescription/samweb/settings/theme-type';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { StageSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/stage-settings/stage-settings.state';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { TransferCallAction } from 'src/app/store/samwin/transfer/transfer.actions';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SettingsState } from 'src/app/store/settings/settings.state';

@Component({
   selector: 'samweb-stage-line-a',
   templateUrl: './stage-line-a.component.html',
   styleUrls: ['./stage-line.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StageLineAComponent implements OnInit, OnDestroy {
   constructor(private store: Store) {}

   @Input()
   public brickInstanceId: string;

   @Select(SamwinLinesStateSelector.getCurrentCall)
   public callInfo$: Observable<SamwinCallInfoRemoteDto>;

   @Select(SamwinLinesStateSelector.isCallInProgress)
   public isCallInProgress$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.canDisconnectLineA)
   public canDisconnectLineA$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.canDisconnectLineB)
   public canDisconnectLineB$: Observable<boolean>;

   @Select(ClientPhoneState.canPhoneMakeCall)
   public canPhoneMakeCall$: Observable<boolean>;

   @Select(ClientPhoneState.isCallRingingAndAcceptable)
   public isCallRingingAndAcceptable$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.isPhoneRinging)
   public isPhoneRinging$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.isLineAOnHold)
   public isLineAOnHold$: Observable<boolean>;

   @Select(ClientPhoneState.isLineAIdle)
   public isLineAIdle$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.canToggleHoldLineA)
   public canToggleHoldLineA$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.getSourceLineStateModel)
   public currentSourceLineStateModel$: Observable<SamwinLineStatusRemoteDto>;

   @Select(SamwinUserStatusState.getNextAutoStatusChangeTime)
   public currentUserStatusDuration$: Observable<moment.Moment>;

   @Select(SamwinUserStatusState.currentStatusInfo)
   public currentUserStatusInfo$: Observable<UserStatusInfoRemoteDto>;

   @Select(TransferState.canTransfer)
   public canTransfer$: Observable<boolean>;

   @Select(SettingsState.appliedTheme)
   public applicationTheme$: Observable<ThemeType>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))
   public featureEnabled$: Observable<boolean>;

   public showHistory$: Observable<boolean>;
   public showCallInfo$: Observable<boolean>;
   public toggleExpertView: boolean;
   private destroy$ = new Subject();

   public answerIcon: IconDefinition = WellKnownIconsEnum.CALLING_ANSWER.icon;
   public dialArrowIcon: IconDefinition = WellKnownIconsEnum.CALLING_DIAL_ARROW.icon;
   public hangUpIcon: IconDefinition = WellKnownIconsEnum.CALLING_HANGUP.icon;
   public cancelIcon: IconDefinition = WellKnownIconsEnum.CANCEL.icon;
   public transferIcon: IconDefinition = WellKnownIconsEnum.CALLING_TRANSFER.icon;
   public holdIcon: IconDefinition = faPause;
   public callHistoryIcon = faHistory;

   ngOnInit() {
      this.showCallInfo$ = this.store.select(StageSettingsState.getShowCallInfo).pipe(map((filterFn) => filterFn(this.brickInstanceId)));
      this.showHistory$ = this.store.select(StageSettingsState.getShowHistory).pipe(map((filterFn) => filterFn(this.brickInstanceId)));
      this.isLineAIdle$
         .pipe(
            tap((isLineAIdle) => {
               this.toggleExpertView = this.toggleExpertView && !isLineAIdle;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   disconnectLineA() {
      this.store.dispatch(new DisconnectLineA());
   }

   acceptOrStartCall() {
      this.store.dispatch(new AcceptOrStartCallAction('accept'));
   }

   call(numberToCall: string) {
      if (isAnonymous(numberToCall)) {
         return;
      }
      this.store.dispatch(new StartNewCallAction(numberToCall));
   }

   transferCall() {
      this.store.dispatch(new TransferCallAction());
   }

   holdLine() {
      this.store.dispatch(new ToggleHoldLineA());
   }

   toggleView() {
      const attendant = this.store.selectSnapshot(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT));
      if (!this.toggleExpertView && attendant) {
         this.toggleExpertView = true;
      } else {
         this.toggleExpertView = false;
      }
   }

   skillInfo(call: SamwinCallInfoRemoteDto) {
      return this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(call.queueId);
   }
}
